import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, map, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { DrugService } from 'src/app/pages/settings/drug-tab/drug-list/drug.service';
import { PharmacyService } from '../pharmacy.service';
import { InventoryDialogData } from '../pharmacy.type';
import { LookupService } from 'src/app/services/lookup.service';

@Component({
  selector: 'app-inventory-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    ReactiveFormsModule,
  ],
  templateUrl: './inventory-dialog.component.html',
})
export class InventoryDialogComponent implements OnInit, OnDestroy {

  inventoryForm: FormGroup;
  isEdit: boolean;
  drugCtrl = new FormControl();
  filteredDrugs$: Observable<CommonDropDown[]>;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<InventoryDialogComponent>,
    private pharmacyService: PharmacyService,
    private drugService: DrugService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils,
    @Inject(MAT_DIALOG_DATA) private data: InventoryDialogData,
    private lookupService: LookupService
  ) {
    this.isEdit = data?.isEdit ?? false;
  }

  ngOnInit(): void {
    this.initForm();
    this.setupDrugAutocomplete();
    this.loadEditData();
  }

  private initForm(): void {
    this.inventoryForm = this.fb.group({
      drugId: [this.data?.inventory?.drugId ?? null, Validators.required],
      sellingPrice: [this.data?.inventory?.sellingPrice ?? null, [Validators.required, Validators.min(0)]],
      gstPercentage: [this.data?.inventory?.gstPercentage ?? 0, [Validators.required, Validators.min(0), Validators.max(100)]],
      reorderLevel: [this.data?.inventory?.reorderLevel ?? 0, [Validators.required, Validators.min(0)]],
    });
  }

  private setupDrugAutocomplete(): void {
    this.filteredDrugs$ = this.drugCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => {
        const search = typeof value === 'string'
          ? value
          : value?.name ?? '';

        return this.lookupService.getDrugDetails(search);
      })
    );
  }

  private loadEditData(): void {
    if (!this.isEdit || !this.data?.inventory) return;

    const inv = this.data.inventory;
    this.drugCtrl.setValue({ id: inv.drugId, name: inv.drugName } as CommonDropDown);
    this.drugCtrl.disable();
  }

  displayDrug(drug?: CommonDropDown): string {
    return drug?.name ?? '';
  }

  onDrugSelected(drug: CommonDropDown): void {
    this.inventoryForm.patchValue({ drugId: drug.id });
  }

  onSave(): void {
    if (this.inventoryForm.invalid) return;

    const payload = this.inventoryForm.getRawValue();

    if (this.isEdit) {
      const updatePayload = { id: this.data.inventory!.id, ...payload };
      this.pharmacyService.updateInventory(updatePayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) =>
            this.handleSaveResponse(response, 'COMMON.UPDATE'),

          error: (error) =>
            this.handleHttpError(error)
        });

    } else {
      this.pharmacyService.addInventory(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) =>
            this.handleSaveResponse(response, 'COMMON.CREATE'),

          error: (error) =>
            this.handleHttpError(error)
        });
    }
  }

  private handleSaveResponse(response: any, messageKey: string): void {
    if (response.isSuccess) {
      this.dialogRef.close(response);
      this.toastr.success(
        this.translationUtils.getTranslatedMessage(messageKey, this.translate.instant('PHARMACY.INVENTORY_ITEM'))
      );
    } else {
      const allErrors = Object.values(response.errors).flat();
      this.toastr.error(allErrors.join('\n'));
    }
  }

  private handleHttpError(error: any): void {
    if (error?.error?.errors) {
      const validationErrors = Object.values(error.error.errors).flat();
      this.toastr.error(validationErrors.join('\n'));
      return;
    }

    if (error?.error?.message) {
      this.toastr.error(error.error.message);
      return;
    }

    this.toastr.error('Something went wrong');
  }

  onClose(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
